export interface IPerformanceMetrics {
  snapshotCreationTime: number[];
  memoryUsage: number[];
  networkLatency: number[];
  renderTime: number[];
  eventProcessingTime: number[];
  domOperationTime: number[];
  compressionTime: number[];
}

export interface IRenderMetrics {
  fps: number;
  frameDrops: number;
  paintTime: number;
  layoutTime: number;
  scriptTime: number;
  loadTime: number;
}

export interface IMemoryMetrics {
  heapUsed: number;
  heapTotal: number;
  heapLimit: number;
  snapshotMemoryUsage: number;
  domNodesCount: number;
  listenerCount: number;
}

export interface INetworkMetrics {
  requestCount: number;
  averageLatency: number;
  errorRate: number;
  dataTransferred: number;
  activeConnections: number;
  websocketLatency: number;
}

export interface IUserInteractionMetrics {
  clickRate: number;
  inputRate: number;
  scrollEvents: number;
  errorCount: number;
  sessionDuration: number;
  activeTime: number;
}

export interface ISystemMetrics {
  cpu: number;
  memory: IMemoryMetrics;
  network: INetworkMetrics;
  render: IRenderMetrics;
  userInteraction: IUserInteractionMetrics;
  timestamp: number;
}

/**
 * MetricsCollector - 시스템 성능 메트릭을 수집하고 분석하는 클래스
 */
export class MetricsCollector {
  private _metrics: IPerformanceMetrics;
  private _isCollecting: boolean = false;
  private _collectionInterval: number = 1000; // 1초마다 수집
  private _maxDataPoints: number = 300; // 5분치 데이터 (1초 간격)
  private _intervalId: number | null = null;
  private _performanceObserver: PerformanceObserver | null = null;
  private _startTime: number = Date.now();
  private _userInteractionCount: number = 0;
  private _networkRequests: Map<string, { startTime: number; size: number }> = new Map();

  constructor() {
    this._metrics = {
      snapshotCreationTime: [],
      memoryUsage: [],
      networkLatency: [],
      renderTime: [],
      eventProcessingTime: [],
      domOperationTime: [],
      compressionTime: [],
    };

    this._setupPerformanceObserver();
  }

  /**
   * 메트릭 수집을 시작합니다
   */
  public startCollection(): void {
    if (this._isCollecting) {
      return;
    }

    this._isCollecting = true;
    this._startTime = Date.now();
    
    this._intervalId = window.setInterval(() => {
      this._collectSystemMetrics();
    }, this._collectionInterval);

    // 사용자 상호작용 추적 시작
    this._setupUserInteractionTracking();
    
    console.log('Metrics collection started');
  }

  /**
   * 메트릭 수집을 중단합니다
   */
  public stopCollection(): void {
    if (!this._isCollecting) {
      return;
    }

    this._isCollecting = false;

    if (this._intervalId) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }

    if (this._performanceObserver) {
      this._performanceObserver.disconnect();
    }

    this._cleanupUserInteractionTracking();
    
    console.log('Metrics collection stopped');
  }

  /**
   * 스냅샷 생성 시간을 기록합니다
   */
  public recordSnapshotCreation(duration: number): void {
    this._addMetric('snapshotCreationTime', duration);
  }

  /**
   * 이벤트 처리 시간을 기록합니다
   */
  public recordEventProcessing(duration: number): void {
    this._addMetric('eventProcessingTime', duration);
  }

  /**
   * DOM 작업 시간을 기록합니다
   */
  public recordDOMOperation(duration: number): void {
    this._addMetric('domOperationTime', duration);
  }

  /**
   * 압축 시간을 기록합니다
   */
  public recordCompression(duration: number): void {
    this._addMetric('compressionTime', duration);
  }

  /**
   * 네트워크 요청을 추적합니다
   */
  public trackNetworkRequest(id: string, size: number = 0): void {
    this._networkRequests.set(id, {
      startTime: performance.now(),
      size,
    });
  }

  /**
   * 네트워크 응답을 기록합니다
   */
  public recordNetworkResponse(id: string): void {
    const request = this._networkRequests.get(id);
    if (request) {
      const latency = performance.now() - request.startTime;
      this._addMetric('networkLatency', latency);
      this._networkRequests.delete(id);
    }
  }

  /**
   * 현재 성능 메트릭을 가져옵니다
   */
  public getMetrics(): IPerformanceMetrics {
    return { ...this._metrics };
  }

  /**
   * 현재 시스템 상태를 가져옵니다
   */
  public getCurrentSystemMetrics(): ISystemMetrics {
    return {
      cpu: this._getCPUUsage(),
      memory: this._getMemoryMetrics(),
      network: this._getNetworkMetrics(),
      render: this._getRenderMetrics(),
      userInteraction: this._getUserInteractionMetrics(),
      timestamp: Date.now(),
    };
  }

  /**
   * 메트릭 요약 정보를 가져옵니다
   */
  public getMetricsSummary(): {
    averages: { [key in keyof IPerformanceMetrics]: number };
    peaks: { [key in keyof IPerformanceMetrics]: number };
    trends: { [key in keyof IPerformanceMetrics]: 'improving' | 'degrading' | 'stable' };
  } {
    const averages: any = {};
    const peaks: any = {};
    const trends: any = {};

    Object.entries(this._metrics).forEach(([key, values]) => {
      if (values.length === 0) {
        averages[key] = 0;
        peaks[key] = 0;
        trends[key] = 'stable';
        return;
      }

      averages[key] = values.reduce((sum: number, val: number) => sum + val, 0) / values.length;
      peaks[key] = Math.max(...values);
      trends[key] = this._calculateTrend(values);
    });

    return { averages, peaks, trends };
  }

  /**
   * 성능 경고를 확인합니다
   */
  public getPerformanceAlerts(): Array<{
    type: 'warning' | 'critical';
    metric: string;
    value: number;
    threshold: number;
    message: string;
  }> {
    const alerts: Array<{
      type: 'warning' | 'critical';
      metric: string;
      value: number;
      threshold: number;
      message: string;
    }> = [];
    const thresholds = {
      snapshotCreationTime: { warning: 100, critical: 500 }, // ms
      memoryUsage: { warning: 50, critical: 100 }, // MB
      networkLatency: { warning: 1000, critical: 3000 }, // ms
      renderTime: { warning: 16, critical: 33 }, // ms (60fps / 30fps)
      eventProcessingTime: { warning: 10, critical: 50 }, // ms
    };

    Object.entries(thresholds).forEach(([metric, threshold]) => {
      const values = this._metrics[metric as keyof IPerformanceMetrics];
      if (values.length === 0) return;

      const latest = values[values.length - 1];
      const average = values.reduce((sum: number, val: number) => sum + val, 0) / values.length;

      if (latest > threshold.critical || average > threshold.critical * 0.8) {
        alerts.push({
          type: 'critical',
          metric,
          value: latest,
          threshold: threshold.critical,
          message: `${metric} is critically high: ${latest.toFixed(2)}ms`,
        });
      } else if (latest > threshold.warning || average > threshold.warning * 0.8) {
        alerts.push({
          type: 'warning',
          metric,
          value: latest,
          threshold: threshold.warning,
          message: `${metric} is elevated: ${latest.toFixed(2)}ms`,
        });
      }
    });

    return alerts;
  }

  /**
   * 메트릭을 내보냅니다
   */
  public exportMetrics(): string {
    const data = {
      collectionPeriod: {
        start: this._startTime,
        end: Date.now(),
        duration: Date.now() - this._startTime,
      },
      metrics: this._metrics,
      summary: this.getMetricsSummary(),
      alerts: this.getPerformanceAlerts(),
      systemInfo: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        cookieEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine,
      },
    };

    return JSON.stringify(data, null, 2);
  }

  /**
   * 메트릭을 초기화합니다
   */
  public clearMetrics(): void {
    Object.keys(this._metrics).forEach(key => {
      this._metrics[key as keyof IPerformanceMetrics] = [];
    });
    this._userInteractionCount = 0;
    this._networkRequests.clear();
    this._startTime = Date.now();
  }

  /**
   * 리소스를 정리합니다
   */
  public destroy(): void {
    this.stopCollection();
    this.clearMetrics();
  }

  /**
   * 메트릭 값을 추가합니다
   */
  private _addMetric(key: keyof IPerformanceMetrics, value: number): void {
    const metrics = this._metrics[key];
    metrics.push(value);

    // 최대 데이터 포인트 수 제한
    if (metrics.length > this._maxDataPoints) {
      metrics.shift();
    }
  }

  /**
   * 시스템 메트릭을 수집합니다
   */
  private _collectSystemMetrics(): void {
    // 메모리 사용량 수집
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      this._addMetric('memoryUsage', memory.usedJSHeapSize / (1024 * 1024)); // MB
    }

    // 렌더링 성능 수집
    const renderTime = this._measureRenderTime();
    if (renderTime > 0) {
      this._addMetric('renderTime', renderTime);
    }
  }

  /**
   * Performance Observer를 설정합니다
   */
  private _setupPerformanceObserver(): void {
    if (!('PerformanceObserver' in window)) {
      return;
    }

    try {
      this._performanceObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        
        entries.forEach(entry => {
          switch (entry.entryType) {
            case 'navigation':
              console.log('Navigation timing:', entry);
              break;
            case 'paint':
              console.log('Paint timing:', entry.name, entry.startTime);
              break;
            case 'measure':
              if (entry.name.startsWith('debug-time-machine')) {
                this._addMetric('eventProcessingTime', entry.duration);
              }
              break;
          }
        });
      });

      this._performanceObserver.observe({ 
        entryTypes: ['navigation', 'paint', 'measure'] 
      });
    } catch (error) {
      console.warn('Failed to setup PerformanceObserver:', error);
    }
  }

  /**
   * 사용자 상호작용 추적을 설정합니다
   */
  private _setupUserInteractionTracking(): void {
    const interactionEvents = ['click', 'input', 'keydown', 'scroll'];
    
    interactionEvents.forEach(eventType => {
      document.addEventListener(eventType, this._handleUserInteraction, {
        passive: true,
        capture: true,
      });
    });
  }

  /**
   * 사용자 상호작용 추적을 정리합니다
   */
  private _cleanupUserInteractionTracking(): void {
    const interactionEvents = ['click', 'input', 'keydown', 'scroll'];
    
    interactionEvents.forEach(eventType => {
      document.removeEventListener(eventType, this._handleUserInteraction, true);
    });
  }

  /**
   * 사용자 상호작용을 처리합니다
   */
  private _handleUserInteraction = (): void => {
    this._userInteractionCount++;
  };

  /**
   * 렌더링 시간을 측정합니다
   */
  private _measureRenderTime(): number {
    // requestAnimationFrame을 사용한 간단한 렌더링 시간 측정
    performance.mark('render-start');
    
    requestAnimationFrame(() => {
      performance.mark('render-end');
      performance.measure('render-time', 'render-start', 'render-end');
      
      const measure = performance.getEntriesByName('render-time')[0];
      if (measure) {
        return measure.duration;
      }
    });

    return 0; // 비동기이므로 즉시 반환할 수 없음
  }

  /**
   * CPU 사용량을 추정합니다
   */
  private _getCPUUsage(): number {
    // 브라우저에서는 정확한 CPU 사용량을 얻기 어려움
    // 렌더링 성능 기반으로 추정
    const renderTimes = this._metrics.renderTime.slice(-10); // 최근 10개
    if (renderTimes.length === 0) return 0;

    const averageRenderTime = renderTimes.reduce((sum: number, time: number) => sum + time, 0) / renderTimes.length;
    
    // 16.67ms (60fps)를 기준으로 CPU 사용률 추정
    return Math.min(100, (averageRenderTime / 16.67) * 100);
  }

  /**
   * 메모리 메트릭을 가져옵니다
   */
  private _getMemoryMetrics(): IMemoryMetrics {
    const defaults: IMemoryMetrics = {
      heapUsed: 0,
      heapTotal: 0,
      heapLimit: 0,
      snapshotMemoryUsage: 0,
      domNodesCount: document.querySelectorAll('*').length,
      listenerCount: 0, // 정확한 측정이 어려움
    };

    if ('memory' in performance) {
      const memory = (performance as any).memory;
      return {
        ...defaults,
        heapUsed: memory.usedJSHeapSize,
        heapTotal: memory.totalJSHeapSize,
        heapLimit: memory.jsHeapSizeLimit,
      };
    }

    return defaults;
  }

  /**
   * 네트워크 메트릭을 가져옵니다
   */
  private _getNetworkMetrics(): INetworkMetrics {
    const latencies = this._metrics.networkLatency;
    const averageLatency = latencies.length > 0 
      ? latencies.reduce((sum: number, val: number) => sum + val, 0) / latencies.length 
      : 0;

    return {
      requestCount: latencies.length,
      averageLatency,
      errorRate: 0, // 별도 추적 필요
      dataTransferred: 0, // 별도 추적 필요
      activeConnections: this._networkRequests.size,
      websocketLatency: 0, // 별도 측정 필요
    };
  }

  /**
   * 렌더링 메트릭을 가져옵니다
   */
  private _getRenderMetrics(): IRenderMetrics {
    const renderTimes = this._metrics.renderTime;
    const averageRenderTime = renderTimes.length > 0
      ? renderTimes.reduce((sum: number, val: number) => sum + val, 0) / renderTimes.length
      : 0;

    const fps = averageRenderTime > 0 ? 1000 / averageRenderTime : 60;
    const frameDrops = renderTimes.filter(time => time > 16.67).length;

    return {
      fps: Math.min(60, fps),
      frameDrops,
      paintTime: averageRenderTime,
      layoutTime: 0, // Performance API에서 추출 필요
      scriptTime: 0, // Performance API에서 추출 필요
      loadTime: performance.timing ? performance.timing.loadEventEnd - performance.timing.navigationStart : 0,
    };
  }

  /**
   * 사용자 상호작용 메트릭을 가져옵니다
   */
  private _getUserInteractionMetrics(): IUserInteractionMetrics {
    const sessionDuration = Date.now() - this._startTime;
    
    return {
      clickRate: this._userInteractionCount / (sessionDuration / 60000), // per minute
      inputRate: 0, // 별도 추적 필요
      scrollEvents: 0, // 별도 추적 필요
      errorCount: 0, // 별도 추적 필요
      sessionDuration,
      activeTime: sessionDuration, // 실제로는 포커스 시간만 계산 필요
    };
  }

  /**
   * 트렌드를 계산합니다
   */
  private _calculateTrend(values: number[]): 'improving' | 'degrading' | 'stable' {
    if (values.length < 5) {
      return 'stable';
    }

    const recent = values.slice(-5);
    const earlier = values.slice(-10, -5);
    
    if (earlier.length === 0) {
      return 'stable';
    }

    const recentAvg = recent.reduce((sum: number, val: number) => sum + val, 0) / recent.length;
    const earlierAvg = earlier.reduce((sum: number, val: number) => sum + val, 0) / earlier.length;
    
    const change = (recentAvg - earlierAvg) / earlierAvg;
    
    if (change > 0.1) return 'degrading'; // 10% 증가는 성능 저하
    if (change < -0.1) return 'improving'; // 10% 감소는 성능 개선
    return 'stable';
  }
}
